import type { JSX } from "react";
import { ChildrenSection } from "./ChildrenSection";
import { ClaimBenefitToggle } from "./ClaimBenefitToggle";
import { IncludePartnerToggle } from "./IncludePartnerToggle";
import { PersonFormSection } from "./PersonFormSection";
import type { useHouseholdState } from "../hooks/useHouseholdState";

interface HouseholdFormFieldsProps {
  /**
   * Form mode:
   * - 'full': Shows all fields (pensions, charity, etc.) - used by Calculator and Options pages
   * - 'basic': Shows only basic fields (salary, tax code) - used by Household settings page
   */
  mode?: 'full' | 'basic';

  /**
   * Household state from useHouseholdState hook
   */
  householdState: ReturnType<typeof useHouseholdState>;
}

/**
 * HouseholdFormFields - Just the form fields without any wrapper
 *
 * Used by Calculator and Household settings pages.
 * Renders only the form fields - parent component handles FormCard/form wrapper.
 * Note: Tax year selection is handled externally (e.g., CompactTaxYearSelector on Calculator page).
 */
export function HouseholdFormFields({
  mode = 'full',
  householdState,
}: HouseholdFormFieldsProps): JSX.Element {
  return (
    <>
      <PersonFormSection
        mode={mode}
        data={householdState.primary}
        setData={householdState.setPrimary}
      />

      <ChildrenSection
        childrenData={householdState.children}
        onAdd={() => householdState.setChildren([...householdState.children, { age: 0 }])}
        onRemove={(i) => householdState.setChildren(householdState.children.filter((_, idx) => idx !== i))}
        onChange={(i, age) => {
          const updated = [...householdState.children];
          updated[i] = { age };
          householdState.setChildren(updated);
        }}
      />

      {householdState.hasChildren && (
        <>
          <ClaimBenefitToggle
            checked={householdState.claimsChildBenefit}
            onChange={householdState.setClaimsChildBenefit}
          />
          <IncludePartnerToggle
            checked={householdState.includePartner}
            onChange={householdState.setIncludePartner}
          />
          {householdState.includePartner && (
            <PersonFormSection
              mode={mode}
              data={householdState.partner}
              setData={householdState.setPartner}
              title="Partner"
            />
          )}
        </>
      )}
    </>
  );
}
